/**
 * The application router. All routes that live inside the pageLayout content
 * section are defined here (see ROUTE_TREE at the bottom of the file).
 *
 * Route components are lazy-loaded, grouped into webpack chunks by feature area
 * via the `chunk-*` webpackChunkName literals. Home is eager — it's the default
 * route and always needed on first paint. A single Suspense boundary wraps the
 * Switch: only the matched route renders, so one boundary is effectively
 * per-route. The fallback is the token-driven app-shell placeholder.
 */
import React, { Suspense, lazy } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import PropTypes from 'prop-types'
import { NoMatch } from '@adc/ui-morpheus'
import { usePermissions } from './common/authorization/PermissionsProvider'
import { MorpheusAppShellPlaceholder } from './common/appShell/MorpheusAppShellPlaceholder'
// Eager: default route, always on first paint.
import homePage from './common/components/homePage'
// Lazy route chunks, grouped by feature area via the chunk-* webpackChunkName
// literals (guardrail: routerChunks.test).
//
// SSR note: the server renders with `renderToString`, which does NOT support
// Suspense — a suspending React.lazy component throws "The server did not
// finish this Suspense boundary". So routes are isomorphic: EAGER (sync
// `require`) in the server bundle so nothing suspends during renderToString,
// and LAZY (`import()` code-split chunk) in the client bundle. The branch is on
// foundation's `__SERVER_BUNDLE__` DefinePlugin constant, so webpack strips the
// dead branch per bundle — the server never bundles separate chunks (it is
// `maxChunks: 1`) and the client never pulls the eager `require` into main.
const iso = (eager, load) => (__SERVER_BUNDLE__ ? eager : lazy(load))

const workQueuePage = iso(
  __SERVER_BUNDLE__ && require('./common/components/workQueuePage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-workspace" */ './common/components/workQueuePage'
    )
)
const chatNotebookPage = iso(
  __SERVER_BUNDLE__ && require('./common/components/chatNotebookPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-workspace" */ './common/components/chatNotebookPage'
    )
)
const communicationCenterPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/components/communicationCenterPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-workspace" */ './common/components/communicationCenterPage'
    )
)
const usersPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/usersPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/usersPage'
    )
)
const userManagementPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/userManagementPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/userManagementPage'
    )
)
const auditPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/auditPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/auditPage'
    )
)
const organizationAuditPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/organizationAuditPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/organizationAuditPage'
    )
)
const financePage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/financeTransactionLogHistoryPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-insights" */ './common/containers/financeTransactionLogHistoryPage'
    )
)
const clientAuthorizationPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/clientAuthorizationPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-config" */ './common/containers/clientAuthorizationPage'
    )
)
const tinyUrlManagementPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/tinyUrlManagementPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-config" */ './common/containers/tinyUrlManagementPage'
    )
)
const invitationsManagementPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/invitationsManagementPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/invitationsManagementPage'
    )
)
const aiEvaluationsPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/aiEvaluationsPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-insights" */ './common/containers/aiEvaluationsPage'
    )
)
const MorpheusLogsPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/morpheusLogsPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-insights" */ './common/containers/morpheusLogsPage'
    )
)
const OrganizationsGridContainer = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/organizations').OrganizationsGridContainer,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/organizations'
    ).then(m => ({ default: m.OrganizationsGridContainer }))
)
const OrganizationsForm = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/organizations/edit').default,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/organizations/edit'
    )
)
const ActivityTimeline = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/activityTimeline').ActivityTimeline,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/activityTimeline'
    ).then(m => ({ default: m.ActivityTimeline }))
)
const OrganizationMembers = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/organizationMembers').OrganizationMembers,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/organizationMembers'
    ).then(m => ({ default: m.OrganizationMembers }))
)
const CommunicationsPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/communications').default,
  () =>
    import(
      /* webpackChunkName: "chunk-onboarding" */ './common/containers/communications'
    )
)
const transactionsPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/transactionsPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/transactionsPage'
    )
)
const TransactionDetailPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/transactionDetailPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/transactionDetailPage'
    )
)
const PropertyRedirect = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/transactionDetailPage/PropertyRedirect')
      .default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/transactionDetailPage/PropertyRedirect'
    )
)
const TransactionByIdPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/transactionDetailPage/TransactionByIdPage')
      .default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/transactionDetailPage/TransactionByIdPage'
    )
)
const VenueTemplatePage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/venueTemplatePage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-config" */ './common/containers/venueTemplatePage'
    )
)
const VenueTemplateHistoryPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/venueTemplateHistoryPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-config" */ './common/containers/venueTemplateHistoryPage'
    )
)
const VenueTemplatesPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/venueTemplatesPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-config" */ './common/containers/venueTemplatesPage'
    )
)
const EventTemplatePage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/eventTemplatePage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-config" */ './common/containers/eventTemplatePage'
    )
)
const EventTemplatesPage = iso(
  __SERVER_BUNDLE__ &&
    require('./common/containers/eventTemplatesPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-config" */ './common/containers/eventTemplatesPage'
    )
)
const VenuesPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/venuesPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/venuesPage'
    )
)
const EventsPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/eventsPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/eventsPage'
    )
)
const EventPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/eventPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/eventPage'
    )
)
const EventHistoryPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/eventHistoryPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/eventHistoryPage'
    )
)
const VenuePage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/venuePage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/venuePage'
    )
)
const VenueHistoryPage = iso(
  __SERVER_BUNDLE__ && require('./common/containers/venueHistoryPage').default,
  () =>
    import(
      /* webpackChunkName: "chunk-asset" */ './common/containers/venueHistoryPage'
    )
)

/**
 * Route authorization comes from the MANIFEST — the same source the nav uses
 * (createPermissions(...).can(key, 'read')). There are no per-route
 * `permissions`/`resource` props: a route names its manifest entry and the
 * user's authorities (redux) are evaluated against it consistently.
 */
const ManifestRoute = ({
  path,
  exact = true,
  manifestKey,
  component: Component,
}) => {
  const { canRead } = usePermissions(manifestKey)
  const allowed = canRead()
  return (
    <Route
      path={path}
      exact={exact}
      render={props =>
        allowed ? <Component {...props} /> : <Redirect to='/' />
      }
    />
  )
}

ManifestRoute.propTypes = {
  path: PropTypes.string.isRequired,
  exact: PropTypes.bool,
  manifestKey: PropTypes.string.isRequired,
  component: PropTypes.elementType.isRequired,
}

// Route-gating feature flags (whole-route on/off).
const FLAGS = {
  myWork: 'fe.resi.btbhome.enable-my-work-queue',
  finance: 'fe.resi.is_morpheus_finance_section_enabled',
}

// ── Route tree ─────────────────────────────────────────────────────────────
// Nesting mirrors resource coupling: a `group` declares its manifest entry +
// index component once; nested CRUD/detail routes inherit that key and share
// the path prefix. Paths are absolute — there is no match.url threading.
const sub = (seg, component, opts = {}) => ({ seg, component, ...opts })

const page = (path, component, manifestKey, opts = {}) => [
  {
    path,
    component,
    manifestKey,
    exact: opts.exact !== false,
    flag: opts.flag,
  },
]

const group = (prefix, manifestKey, index, children = [], opts = {}) => [
  { path: prefix, component: index, manifestKey, exact: true, flag: opts.flag },
  ...children.map(c => ({
    path: `${prefix}/${c.seg}`,
    component: c.component,
    manifestKey,
    exact: c.exact !== false,
    flag: c.flag ?? opts.flag,
  })),
]

/**
 * @param _match unused — routes are absolute (kept for the MasterLayout call
 *   signature). @param featureFlags gates whole routes on/off.
 */
const router = (_match, featureFlags = {}) => {
  const routes = ROUTE_TREE.flat().filter(r => !r.flag || featureFlags[r.flag])
  return (
    <Suspense fallback={<MorpheusAppShellPlaceholder />}>
      <Switch>
        {routes.map(r => (
          <ManifestRoute
            key={r.path}
            path={r.path}
            exact={r.exact}
            manifestKey={r.manifestKey}
            component={r.component}
          />
        ))}
        <Route component={NoMatch} />
      </Switch>
    </Suspense>
  )
}

export default router

// ── Route definitions ─────────────────────────────────────────────────────
// The single list of routes. Everything above is the machinery that renders
// and gates them.
const ROUTE_TREE = [
  page('/', homePage, 'home'),
  page('/chat-notebook', chatNotebookPage, 'chatNotebook'),
  page('/work-queue/:userId?', workQueuePage, 'myWork', { flag: FLAGS.myWork }),

  group('/user-management', 'user', usersPage, [
    sub(':id/edit', userManagementPage),
    sub(':id/history', auditPage),
  ]),

  group('/transactions', 'transaction', transactionsPage, [
    sub(
      'property/:propertyId/transaction/:transactionId',
      TransactionDetailPage
    ),
    sub('property/:propertyId/transaction', TransactionDetailPage),
    sub('property/:propertyId/listing/:listingId', PropertyRedirect),
    sub('property/:propertyId', TransactionDetailPage),
    sub(':transactionId/lead', TransactionByIdPage),
    sub(':transactionId', TransactionByIdPage),
  ]),

  group('/organizations', 'organization', OrganizationsGridContainer, [
    sub(':id/edit', OrganizationsForm),
    sub(':id/history', organizationAuditPage),
    sub('create', OrganizationsForm),
    sub('activitytimeline', ActivityTimeline),
    sub('members', OrganizationMembers),
  ]),

  group('/tiny-url', 'tinyUrl', tinyUrlManagementPage, [
    sub(':id/edit', tinyUrlManagementPage),
    sub('create', tinyUrlManagementPage),
  ]),

  group('/schedule/events', 'event', EventsPage, [
    sub('create', EventPage),
    sub(':id/edit', EventPage),
    sub(':id/history', EventHistoryPage),
  ]),
  group('/schedule/venues', 'venue', VenuesPage, [
    sub('create', VenuePage),
    sub(':id/edit', VenuePage),
    sub(':id/history', VenueHistoryPage),
  ]),
  group('/schedule/event-templates', 'eventTemplate', EventTemplatesPage, [
    sub('create', EventTemplatePage),
    sub(':id/edit', EventTemplatePage),
  ]),
  group('/schedule/venue-templates', 'venueTemplate', VenueTemplatesPage, [
    sub('create', VenueTemplatePage),
    sub(':id/edit', VenueTemplatePage),
    sub(':id/history', VenueTemplateHistoryPage),
  ]),

  page('/client-authorization', clientAuthorizationPage, 'clientAuthorization'),
  page('/communications', CommunicationsPage, 'communication'),
  page('/invitations', invitationsManagementPage, 'invitation'),
  page('/ai-evaluations', aiEvaluationsPage, 'aiEvaluations'),
  page('/communication-center', communicationCenterPage, 'communicationCenter'),
  page(
    '/finance/transaction-history',
    financePage,
    'financeTransactionHistory',
    {
      flag: FLAGS.finance,
    }
  ),
  page('/sync/morpheus-logs', MorpheusLogsPage, 'morpheusLogs', {
    exact: false,
  }),
]
